import { useEffect, useRef } from "react"
import { Avatar, Box, CircularProgress, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-solid-svg-icons"
import { faComments } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { ChatMessage, UserType } from "./orderChatTypes"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { EmptyContent } from "../skeleton/EmptyContent"

const useStyles = makeStyles()((theme) => ({
    container: {
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    bubble: {
        borderRadius: "0.75rem",
        padding: "1rem",
        gap: "1rem",
        display: "flex",
        flexDirection: "column",
        backgroundColor: tailwindPalette.slate[100],
        color: tailwindPalette.slate[900],
    },
    bubbleOther: {
        backgroundColor: tailwindPalette.slate[500],
        color: theme.palette.common.white,
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
    },
    authorNameOther: {
        color: theme.palette.common.white,
    },
    timestampOther: {
        color: tailwindPalette.slate[300],
    },
    avatar: {
        width: "2.75rem",
        height: "2.75rem",
    },
    avatarSelf: {
        backgroundColor: tailwindPalette.purple[600],
        color: theme.palette.common.white,
    },
    avatarOther: {
        backgroundColor: tailwindPalette.sky[600],
        color: theme.palette.common.white,
    },
    messageContent: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    centeredContainer: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}))

type OrderChatMessageListProps = Readonly<{
    messages: ChatMessage[]
    isLoading: boolean
}>

function getInitials(displayName: string): string {
    return displayName
        .split(" ")
        .slice(0, 2)
        .map((part) => part[0] ?? "")
        .join("")
        .toUpperCase()
}

function MessageHeader({
    message,
    classes,
    isCustomer,
}: {
    message: ChatMessage
    classes: ReturnType<typeof useStyles>["classes"]
    isCustomer: boolean
}) {
    const { cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const dateTime = dateTimeFactory.fromApi(message.created)
    const dateTimeString = dateTimeFactory.formatDateTime(dateTime)
    const avatarClass = cx(classes.avatar, isCustomer ? classes.avatarSelf : classes.avatarOther)

    return (
        <Box className={classes.header}>
            {isCustomer ? (
                <Avatar className={avatarClass}>{getInitials(message.creator.displayName)}</Avatar>
            ) : (
                <Avatar className={avatarClass}>
                    <FontAwesomeIcon icon={faShop} />
                </Avatar>
            )}
            <Stack alignItems="flex-start">
                <Typography variant="body1" className={!isCustomer ? classes.authorNameOther : undefined}>
                    {message.creator.displayName}
                </Typography>
                <Typography
                    variant="body2"
                    color={isCustomer ? "text.secondary" : undefined}
                    className={!isCustomer ? classes.timestampOther : undefined}
                >
                    {dateTimeString}
                </Typography>
            </Stack>
        </Box>
    )
}

function MessageContent({
    message,
    classes,
}: {
    message: ChatMessage
    classes: ReturnType<typeof useStyles>["classes"]
}) {
    const isCustomer = message.creator.userType === UserType.USER

    return (
        <>
            <MessageHeader message={message} classes={classes} isCustomer={isCustomer} />
            <Typography className={classes.messageContent}>{message.body}</Typography>
        </>
    )
}

export function OrderChatMessageList({ messages, isLoading }: OrderChatMessageListProps) {
    const { classes, cx } = useStyles()
    const endRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [messages.length])

    if (isLoading) {
        return (
            <Box className={classes.centeredContainer}>
                <CircularProgress size={32} />
            </Box>
        )
    }

    if (messages.length === 0) {
        return (
            <Box className={classes.centeredContainer}>
                <LocalizedStrict id="order-chat-empty" attrs={{ title: true, description: true }}>
                    <EmptyContent
                        icon={faComments}
                        backgroundColor={tailwindPalette.sky[500]}
                        title="No messages yet"
                        description="Start the conversation!"
                    />
                </LocalizedStrict>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            {messages.map((message) => {
                const isCustomer = message.creator.userType === UserType.USER
                return (
                    <Box key={message.id} className={cx(classes.bubble, !isCustomer && classes.bubbleOther)}>
                        <MessageContent message={message} classes={classes} />
                    </Box>
                )
            })}
            <div ref={endRef} />
        </Box>
    )
}
